import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    getAllPublications,
    getPublicationById,
    createPublication,
    updatePublication,
    deletePublication,
    updatePublicationOrder,
    reorderPublications,
    Publication
} from '../firebase/publicationService';

// Query keys
export const publicationKeys = {
    all: ['publications'] as const,
    lists: () => [...publicationKeys.all, 'list'] as const,
    detail: (id: string) => [...publicationKeys.all, 'detail', id] as const,
};

// Hooks
export const usePublicationsQuery = () => {
    return useQuery({
        queryKey: publicationKeys.lists(),
        queryFn: getAllPublications,
    });
};

export const usePublicationQuery = (id: string) => {
    return useQuery({
        queryKey: publicationKeys.detail(id),
        queryFn: () => getPublicationById(id),
        enabled: !!id,
    });
};

export const useCreatePublicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: Omit<Publication, 'id' | 'createdAt' | 'updatedAt'>) =>
            createPublication(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: publicationKeys.lists() });
        },
    });
};

export const useUpdatePublicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: {
            id: string;
            data: Partial<Omit<Publication, 'id' | 'createdAt' | 'updatedAt'>>
        }) => updatePublication(id, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: publicationKeys.lists() });
            queryClient.invalidateQueries({ queryKey: publicationKeys.detail(variables.id) });
        },
    });
};

export const useDeletePublicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deletePublication(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: publicationKeys.lists() });
        },
    });
};

export const useUpdatePublicationOrderMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (reorderedPublications: { id: string; order: number }[]) =>
            updatePublicationOrder(reorderedPublications),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: publicationKeys.lists() });
        },
    });
};

export const useReorderPublicationsMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: reorderPublications,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: publicationKeys.lists() });
        },
    });
}; 